import React, { useState } from "react";
import { toast } from "sonner";
import { Upload, X, FileText } from "lucide-react";
import { http } from "@/lib/api";

const SAMPLE = `name,email,phone,visa_type,target_country,timeline,notes,advisor
Jane Doe,jane@example.com,+1 555 0100,Work,Canada,Under 3 months,Ex-FAANG,
John Smith,john@example.com,+44 20 0100,Study,UK,3-6 months,,`;

export default function ImportLeadsModal({ open, onClose, canAssign, onImported }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  if (!open) return null;

  const submit = async () => {
    if (!file) return toast.error("Pick a .csv file");
    setBusy(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await http.post("/leads/import", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`Imported ${data.imported} lead${data.imported === 1 ? "" : "s"}`);
      onImported?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  const downloadSample = () => {
    const blob = new Blob([SAMPLE], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "nextshore-leads-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div
      data-testid="import-leads-modal"
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <div>
            <h2 className="font-serif text-xl">Import leads (CSV)</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {canAssign
                ? "You can bulk-assign leads via the `advisor` column."
                : "Imported leads will be auto-assigned to you."}
            </p>
          </div>
          <button onClick={onClose} data-testid="import-close-btn">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div className="rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 p-5 text-center">
            <FileText className="w-7 h-7 mx-auto text-slate-400 mb-2" />
            <input
              data-testid="import-file-input"
              type="file"
              accept=".csv"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full text-xs text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-[#0B1E3F] file:text-white file:cursor-pointer"
            />
            {file && <div className="mt-2 text-xs text-slate-500">{file.name}</div>}
          </div>

          <div className="text-xs text-slate-500">
            Required columns: <code className="bg-slate-100 px-1 py-0.5 rounded">name, email, phone, visa_type, target_country, timeline</code>. Optional: notes, advisor.
          </div>

          <button
            data-testid="import-download-sample-btn"
            onClick={downloadSample}
            className="text-xs text-[#0B1E3F] hover:text-[#C9A961] underline"
          >
            Download sample CSV template
          </button>

          {result && (
            <div data-testid="import-result" className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-xs text-slate-700">
              <div className="font-semibold text-emerald-800">
                {result.imported} / {result.total_rows} rows imported
              </div>
              {result.errors?.length > 0 && (
                <ul className="mt-2 list-disc pl-5 text-red-700 max-h-32 overflow-y-auto">
                  {result.errors.map((e) => <li key={e}>{e}</li>)}
                </ul>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-4 bg-slate-50 border-t flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-xl border border-slate-200 bg-white hover:bg-slate-100 px-4 py-2 text-sm font-semibold"
          >
            Close
          </button>
          <button
            data-testid="import-submit-btn"
            onClick={submit}
            disabled={!file || busy}
            className="rounded-xl bg-[#0B1E3F] hover:bg-[#162A4D] text-white px-4 py-2 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60"
          >
            <Upload className="w-4 h-4" /> {busy ? "Importing…" : "Import"}
          </button>
        </div>
      </div>
    </div>
  );
}
